/**
 * interior_purpose.ts — THE PURPOSE LAW (2026-09-22): every enterable
 * building resolves to ONE purpose, and PROVENANCE outranks vocabulary.
 *
 * Resolution order (the interiors research programme, §9.1 — "validated
 * override → placement's explicit purpose → deed catalog → settlement
 * program/function provenance → legacy regex fallback"):
 *   0. the validated override — a building bound to a town VENUE (slice E,
 *                               `setPurposeOverride`; none unless a module sets one);
 *   1. the deed catalog       — `deed:<structureType>`: the server's structure
 *                               catalog is the truth about what a player built;
 *   2. the Merchant Hive lot  — `hive:*` is retail by construction;
 *   3. the personal preset    — `preset:home | annex | lounge | shed`;
 *   4. the settlement PROGRAM — the civ-pack program/function tables
 *                               (`civPurpose`), never the opaque key's name;
 *   5. the legacy vocabulary  — the human-readable prefab names;
 *   6. a marker               — `mark:*` carries no provenance: 'generic'.
 *
 * Consumers: the door prompt, the entry toast and the sign inside the door
 * (BuildingInteriorModule), the purpose furnishing plans (interior_plans.ts),
 * `__interior.list`. Pure — no THREE, no DOM; verify:interiors pins it.
 *
 * NOT a service model: a purpose changes what a room LOOKS like and what the
 * door SAYS; authority (stations, leases, transactions) stays a later wave.
 */
import { civPurpose } from './biome_settlements.js';

export type InteriorPurpose =
  | 'residence' | 'apartment' | 'civic' | 'office' | 'retail' | 'hospitality' | 'workshop'
  | 'clinic' | 'bank' | 'garage' | 'guild' | 'theater' | 'transit' | 'lodge' | 'shed' | 'generic';
export type PurposeSource = 'deed' | 'lot' | 'preset' | 'program' | 'name' | 'marker' | 'venue';
export interface PurposeInfo { purpose: InteriorPurpose; label: string; source: PurposeSource }

/** The room name a visitor reads at the door. */
export const PURPOSE_LABEL: Readonly<Record<InteriorPurpose, string>> = {
  residence: 'residence', apartment: 'apartments', civic: 'civic hall', office: 'offices', retail: 'shop',
  hospitality: 'cantina', workshop: 'workshop', clinic: 'clinic', bank: 'bank', garage: 'garage', guild: 'guild hall',
  theater: 'theater', transit: 'shuttleport', lodge: 'lounge', shed: 'shed', generic: 'hall',
};

/** The deed catalog's purposes (server/src/lib/structures.ts STRUCTURE_SPECS —
 *  mirrored by NAME; a type missing here falls through to the vocabulary). */
const DEED_PURPOSE: Readonly<Record<string, InteriorPurpose>> = {
  merchant_tent: 'retail', small_house: 'residence', medium_house: 'residence', large_house: 'residence',
  guild_hall: 'guild', factory: 'workshop', city_hall: 'civic', cantina: 'hospitality', hospital: 'clinic',
  bank: 'bank', garage: 'garage', theater: 'theater', cloning: 'clinic', shuttleport: 'transit',
  // THE SWG SET (2026-09-23)
  xlarge_house: 'residence', hotel: 'hospitality', museum: 'civic', store: 'retail', salon: 'retail', bunker: 'civic',
  market: 'retail', mining_facility: 'workshop', spaceport: 'transit', outpost: 'civic',
};
const PRESET_PURPOSE: Readonly<Record<string, InteriorPurpose>> = { home: 'residence', annex: 'workshop', lounge: 'lodge', shed: 'shed' };

/** The legacy vocabulary, most specific first (a `guild_hall_city1` is a guild
 *  before it is a hall; a `terrace_row_home` is apartments before a home). */
const NAME_RULES: ReadonlyArray<readonly [RegExp, InteriorPurpose]> = [
  [/guild/, 'guild'],
  [/council|chamber|civic|admin|academy|temple|sanctum|monolith/, 'civic'],
  [/hall/, 'civic'],
  [/apart|terrace|duplex|block|complex|tower|spire|habitat|hab_|hab$|quarter|barrack/, 'apartment'],
  [/home|house|villa|manor|lodge|dwell|residen|estate|dome/, 'residence'],
];

/** The purpose a legacy prefab NAME implies — null for an opaque or unknown key. */
export function purposeByName(key: string): InteriorPurpose | null {
  const k = key.toLowerCase();
  for (const [re, p] of NAME_RULES) if (re.test(k)) return p;
  return null;
}

const info = (purpose: InteriorPurpose, source: PurposeSource): PurposeInfo => ({ purpose, label: PURPOSE_LABEL[purpose], source });

// ── THE VALIDATED OVERRIDE (the interiors programme, slice E, 2026-10-01) — the first rung of the order above ────────
// A settlement building BOUND to a town venue (lib/venue_binding.ts: the one building of a town that IS its tavern,
// its clinic) is that venue, whatever its civ program says: `AtlasVenueModule` registers the override per enterable id
// while `platform_flag interior_venues_v1` is on and removes it when it is not. Keyed by the registry id, so nothing
// else can be touched by it; globalThis-backed (the instance-split law). Nothing registers one in node: every gate
// that reads `resolvePurpose` sees the tables alone.
const GP = globalThis as unknown as { __atlasPurposeOverride?: Map<string, InteriorPurpose> };
const purposeOverride: Map<string, InteriorPurpose> = (GP.__atlasPurposeOverride ??= new Map());
/** Set (or with `null` clear) the purpose a registry id resolves to. */
export function setPurposeOverride(id: string, purpose: InteriorPurpose | null): void {
  if (purpose) purposeOverride.set(id, purpose); else purposeOverride.delete(id);
}
/** The ids that carry an override (a lever's diagnostic). */
export function purposeOverrides(): ReadonlyMap<string, InteriorPurpose> { return purposeOverride; }

/** Resolve an enterable's purpose from its registry id + key (THE PURPOSE LAW). */
export function resolvePurpose(id: string, key: string): PurposeInfo {
  const bound = purposeOverride.get(id);
  if (bound) return info(bound, 'venue');
  if (id.startsWith('deed:') || key.startsWith('deed:')) {
    const type = key.replace(/^deed:/, '');
    return info(DEED_PURPOSE[type] ?? purposeByName(type) ?? 'generic', 'deed');
  }
  if (id.startsWith('hivelot:') || key.startsWith('hive:')) return info('retail', 'lot');
  if (id.startsWith('preset:')) return info(PRESET_PURPOSE[id.slice('preset:'.length)] ?? 'residence', 'preset');
  if (id.startsWith('mark:')) return info('generic', 'marker');
  const program = civPurpose(key);
  if (program) return info(program, 'program');
  const byName = purposeByName(key);
  if (byName) return info(byName, 'name');
  return info('generic', 'name');
}

/** The prompt suffix: the label, unless the name already says it. */
export function purposeSuffix(name: string, p: PurposeInfo): string {
  const n = name.toLowerCase();
  if (p.purpose === 'generic') return '';
  const head = p.label.split(' ')[0];
  return n.includes(head) ? '' : ` · ${p.label}`;
}
